import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { jobsAPI } from "@/lib/api";
import { useTranslation, EUROPEAN_COUNTRIES, POSITIONS_LIST, COUNTRY_FLAGS } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Briefcase, MapPin, Clock, ArrowRight, Building2, ChevronRight, Search, Home, Globe
} from "lucide-react";

const BASE_URL = "https://staffera.eu";
const EMPLOYMENT_LABELS = {
  full_time: "Full-time", part_time: "Part-time", temporary: "Temporary",
  seasonal: "Seasonal", internship: "Internship",
};
const EMPLOYMENT_SCHEMA = {
  full_time: "FULL_TIME", part_time: "PART_TIME", temporary: "TEMPORARY",
  seasonal: "TEMPORARY", internship: "INTERN",
};

function Breadcrumbs({ items }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm font-body text-slate-500 mb-4">
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            {item.href ? (
              <Link to={item.href} className="hover:text-emerald-600 transition-colors">{item.label}</Link>
            ) : (
              <span className="text-slate-700">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function NavBar() {
  const navigate = useNavigate();
  return (
    <header className="bg-navy-900 border-b border-navy-800">
      <div className="max-w-5xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
        <div onClick={() => navigate("/")} className="cursor-pointer flex-shrink-0"><Logo size="md" className="text-white" /></div>
        <div className="flex items-center gap-2">
          <LanguageSelector variant="dark" />
          <Button variant="ghost" className="text-slate-300 hover:text-white text-xs sm:text-sm px-2 sm:px-3" onClick={() => navigate("/auth")}>Log in</Button>
          <Button className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs sm:text-sm font-semibold rounded-sm px-2 sm:px-3" onClick={() => navigate("/auth?mode=register&role=candidate")}>Sign up</Button>
        </div>
      </div>
    </header>
  );
}

// ============================
// JOB DETAIL PAGE
// ============================
export function JobDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, tc, tp } = useTranslation();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await jobsAPI.getPublicBySlug(slug);
        setJob(res.data);
      } catch { setNotFound(true); }
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-slate-50"><NavBar /><div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div></div>;

  if (notFound || !job) return (
    <div className="min-h-screen bg-slate-50">
      <NavBar />
      <Helmet><meta name="robots" content="noindex" /><title>Job not found - Staffera</title></Helmet>
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-sans font-bold text-2xl text-slate-900 mb-3">Job not found</h1>
        <p className="text-slate-500 font-body mb-6">This job may have been removed or expired.</p>
        <Button onClick={() => navigate("/jobs")} className="bg-emerald-500 hover:bg-emerald-600 text-white">Browse all jobs</Button>
      </div>
    </div>
  );

  const isExpired = job.status !== "active" || (job.expires_at && new Date(job.expires_at) < new Date());
  const locationParts = [job.city, job.region, tc(job.country)].filter(Boolean);
  const locationStr = locationParts.join(", ");
  const seoTitle = `${job.title} - ${locationStr} | Staffera`;
  const seoDesc = `${job.title} at ${job.hotel_name} in ${locationStr}. ${job.description?.substring(0, 120)}...`;
  const canonicalUrl = `${BASE_URL}/jobs/${job.slug}`;

  // JobPosting structured data (only for active jobs)
  const jobPostingLD = !isExpired ? {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": job.published_at,
    ...(job.expires_at && { "validThrough": job.expires_at }),
    "employmentType": EMPLOYMENT_SCHEMA[job.employment_type] || "OTHER",
    "hiringOrganization": {
      "@type": "Organization",
      "name": job.hotel_name,
      ...(job.hotel_website && { "sameAs": job.hotel_website }),
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        ...(job.city && { "addressLocality": job.city }),
        ...(job.region && { "addressRegion": job.region }),
        ...(job.country && { "addressCountry": job.country }),
      }
    },
    "industry": "Hospitality",
    "directApply": true,
    "identifier": { "@type": "PropertyValue", "name": "Staffera", "value": job.job_id },
    ...((job.salary_min || job.salary_max) && {
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": job.salary_currency || "EUR",
        "value": {
          "@type": "QuantitativeValue",
          ...(job.salary_min && job.salary_max ? { "minValue": job.salary_min, "maxValue": job.salary_max } : { "value": job.salary_min || job.salary_max }),
          "unitText": "MONTH",
        }
      }
    }),
    ...(job.experience_years_min && { "experienceRequirements": `${job.experience_years_min}+ years` }),
    ...(job.requirements && { "qualifications": job.requirements }),
  } : null;

  const breadcrumbs = [
    { label: "Staffera", href: "/" },
    { label: "Jobs", href: "/jobs" },
    ...(job.country ? [{ label: tc(job.country), href: `/jobs?country=${job.country}` }] : []),
    { label: job.title },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-detail-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={canonicalUrl} />
        {isExpired && <meta name="robots" content="noindex" />}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Staffera" />
        {jobPostingLD && (
          <script type="application/ld+json">{JSON.stringify(jobPostingLD)}</script>
        )}
      </Helmet>
      <NavBar />

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
        <Breadcrumbs items={breadcrumbs} />

        {isExpired && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-amber-800 font-body" data-testid="job-expired-banner">
            This job posting has expired or been closed. <Link to="/jobs" className="underline font-semibold">Browse active jobs</Link>
          </div>
        )}

        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-lg bg-navy-900 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h1 className="font-sans font-bold text-xl sm:text-2xl text-slate-900 mb-1" data-testid="job-title-h1">{job.title}</h1>
                <p className="text-sm text-slate-500 font-body">{job.hotel_name}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-body">
                <Briefcase className="w-3 h-3 mr-1" />{tp(job.position)}
              </Badge>
              <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-body">
                <MapPin className="w-3 h-3 mr-1" />{locationStr}
              </Badge>
              <Badge className="bg-sky-50 text-sky-700 border-sky-200 font-body">
                {EMPLOYMENT_LABELS[job.employment_type] || job.employment_type}
              </Badge>
              {(job.salary_min || job.salary_max) && (
                <Badge className="bg-gold-50 text-gold-700 border-gold-200 font-body">
                  {job.salary_min && job.salary_max ? `${job.salary_min}-${job.salary_max}` : job.salary_min || job.salary_max} {job.salary_currency || "EUR"}/month
                </Badge>
              )}
              {job.accommodation_provided && (
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 font-body">
                  <Home className="w-3 h-3 mr-1" />Accommodation
                </Badge>
              )}
            </div>

            <div className="prose prose-sm max-w-none mb-6">
              <h2 className="font-sans font-semibold text-base text-slate-900 mb-2">Description</h2>
              <div className="text-sm text-slate-600 font-body leading-relaxed whitespace-pre-wrap">{job.description}</div>
            </div>

            {job.requirements && (
              <div className="mb-6">
                <h2 className="font-sans font-semibold text-base text-slate-900 mb-2">Requirements</h2>
                <div className="text-sm text-slate-600 font-body leading-relaxed whitespace-pre-wrap">{job.requirements}</div>
              </div>
            )}

            {job.experience_years_min && (
              <p className="text-sm text-slate-600 font-body mb-2">Minimum experience: <strong>{job.experience_years_min} years</strong></p>
            )}

            {job.languages_required?.length > 0 && (
              <p className="text-sm text-slate-600 font-body mb-4">Languages: <strong>{job.languages_required.join(", ")}</strong></p>
            )}

            <div className="text-xs text-slate-400 font-body flex items-center gap-3 mb-6">
              <span><Clock className="w-3 h-3 inline mr-1" />Published: {new Date(job.published_at).toLocaleDateString()}</span>
              {job.expires_at && <span>Expires: {new Date(job.expires_at).toLocaleDateString()}</span>}
            </div>

            {!isExpired && (
              <Button size="lg" className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm h-12 text-base"
                onClick={() => navigate("/auth?mode=register&role=candidate")} data-testid="job-apply-btn">
                Apply Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            )}
          </CardContent>
        </Card>

        <div className="text-center">
          <Link to="/jobs" className="text-sm text-emerald-600 hover:text-emerald-700 font-body font-semibold">
            ← Browse all jobs
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================
// JOBS LISTING PAGE
// ============================
export default function JobsListPage() {
  const navigate = useNavigate();
  const { t, tc, tp } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ country: "", position: "" });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadJobs();
    loadStats();
  }, []);

  useEffect(() => {
    loadJobs();
  }, [filters.country, filters.position, page]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.country) params.country = filters.country;
      if (filters.position) params.position = filters.position;
      const res = await jobsAPI.getPublic(params);
      setJobs(res.data.jobs || []);
      setTotal(res.data.total || 0);
    } catch {} finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const res = await jobsAPI.getPublicStats();
      setStats(res.data);
    } catch {}
  };

  const seoTitle = "Hotel Jobs in Europe | Staffera";
  const seoDesc = `Find ${total || ""} hotel jobs across Europe. Receptionist, chef, waiter, housekeeping and more positions at top European hotels.`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-list-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={`${BASE_URL}/jobs`} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={`${BASE_URL}/jobs`} />
      </Helmet>
      <NavBar />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <Breadcrumbs items={[{ label: "Staffera", href: "/" }, { label: "Jobs" }]} />

        <div className="mb-8">
          <h1 className="font-sans font-bold text-2xl sm:text-3xl text-slate-900 mb-2" data-testid="jobs-h1">Hotel Jobs in Europe</h1>
          <p className="text-base text-slate-500 font-body">
            {total > 0 ? `${total} active positions at top European hotels` : "Find your next hospitality career opportunity"}
          </p>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 bg-white shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={filters.country || "all"} onValueChange={v => { setFilters(f => ({ ...f, country: v === "all" ? "" : v })); setPage(1); }}>
                <SelectTrigger className="h-11 font-body" data-testid="jobs-filter-country"><SelectValue placeholder="All countries" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All countries</SelectItem>
                  {(stats?.by_country || []).map(c => (
                    <SelectItem key={c.country} value={c.country}>{COUNTRY_FLAGS[c.country]} {tc(c.country)} ({c.count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.position || "all"} onValueChange={v => { setFilters(f => ({ ...f, position: v === "all" ? "" : v })); setPage(1); }}>
                <SelectTrigger className="h-11 font-body" data-testid="jobs-filter-position"><SelectValue placeholder="All positions" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All positions</SelectItem>
                  {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{tp(p)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white h-11 font-semibold" onClick={() => navigate("/auth?mode=register&role=candidate")}>
                <Search className="w-4 h-4 mr-2" /> Sign up to apply
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Job Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : jobs.length === 0 ? (
          <Card className="border-slate-200 bg-white">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 font-body">No jobs found matching your filters.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map(job => {
              const loc = [job.city, job.region, tc(job.country)].filter(Boolean).join(", ");
              return (
                <Link key={job.job_id} to={`/jobs/${job.slug}`} className="block" data-testid={`public-job-${job.job_id}`}>
                  <Card className="border-slate-200 bg-white shadow-sm hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-navy-900 flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h2 className="font-sans font-semibold text-slate-900 mb-0.5 truncate">{job.title}</h2>
                          <p className="text-xs text-slate-500 font-body mb-2">{job.hotel_name}</p>
                          <div className="flex flex-wrap gap-2">
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-body">{tp(job.position)}</Badge>
                            <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs font-body"><MapPin className="w-3 h-3 mr-1" />{loc}</Badge>
                            <Badge className="bg-sky-50 text-sky-700 border-sky-200 text-xs font-body">{EMPLOYMENT_LABELS[job.employment_type]}</Badge>
                            {(job.salary_min || job.salary_max) && (
                              <Badge className="bg-gold-50 text-gold-700 border-gold-200 text-xs font-body">
                                {job.salary_min && job.salary_max ? `${job.salary_min}-${job.salary_max}` : job.salary_min || job.salary_max} EUR
                              </Badge>
                            )}
                          </div>
                        </div>
                        <ArrowRight className="w-5 h-5 text-slate-300 flex-shrink-0 mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="flex items-center text-sm text-slate-500 font-body px-3">Page {page}</span>
            <Button variant="outline" disabled={jobs.length < 20} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
